import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaMoneyBillWave } from 'react-icons/fa';
import { getCart } from '../actions/cartActions';
import { createOrder } from '../actions/orderActions';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';

const CheckoutPage = () => {
  const [shippingAddress, setShippingAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod'); // Default to Cash on Delivery
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const cart = useSelector(state => state.cart);
  const { loading, error, cartItems } = cart;
  
  const auth = useSelector(state => state.auth);
  const { user } = auth;
  
  const orderCreate = useSelector(state => state.orderCreate);
  const { loading: loadingCreate, error: errorCreate, success: successCreate, order } = orderCreate;
  
  // Redirect to order details after successful order creation
  useEffect(() => {
    if (successCreate && order) {
      navigate(`/order/${order.id}`);
    }
  }, [navigate, successCreate, order]);
  
  useEffect(() => {
    // If cart is empty, redirect to cart page
    if (cartItems.length === 0) {
      navigate('/cart');
    }
    
    // Pre-fill user information if available
    if (user) {
      setShippingAddress(user.address || '');
      setContactPhone(user.phone_number || '');
    }
  }, [navigate, cartItems, user]);
  
  // Load cart data
  useEffect(() => {
    dispatch(getCart());
  }, [dispatch]);
  
  // Calculate order total
  const calculateTotal = () => {
    return cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0).toFixed(2);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!shippingAddress || !contactPhone) {
      return;
    }
    
    // Create order
    dispatch(
      createOrder({
        shipping_address: shippingAddress,
        contact_phone: contactPhone,
        payment_method: paymentMethod
      })
    );
  };
  
  return (
    <div className="checkout-page">
      <h1 className="text-3xl font-bold mb-6">结账</h1>
      
      {loadingCreate && <Loader />}
      {errorCreate && <Message variant="error">{errorCreate}</Message>}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="border-b bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold flex items-center">
                <FaShoppingCart className="mr-2" />
                订单详情
              </h2>
            </div>
            
            <div className="p-6">
              {loading ? (
                <Loader />
              ) : error ? (
                <Message variant="error">{error}</Message>
              ) : (
                <div className="border-b pb-4 mb-4">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex items-center mb-4">
                      <div className="w-16 h-16 mr-4">
                        <img
                          src={item.image_url || '/images/default-wine.jpg'}
                          alt={item.name}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-medium">{item.name}</h3>
                        {item.chinese_name && (
                          <p className="text-sm text-gray-500">{item.chinese_name}</p>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <p className="font-medium">¥{(item.price * item.quantity).toFixed(2)}</p>
                        <p className="text-sm text-gray-500">¥{item.price.toFixed(2)} x {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-between font-bold text-lg">
                <span>总计:</span>
                <span>¥{calculateTotal()}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="border-b bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold flex items-center">
                <FaMoneyBillWave className="mr-2" />
                支付方式
              </h2>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={paymentMethod === 'cod'}
                    onChange={() => setPaymentMethod('cod')}
                    className="mr-2"
                  />
                  <span>货到付款</span>
                </label>
              </div>
              
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="bank_transfer"
                    checked={paymentMethod === 'bank_transfer'}
                    onChange={() => setPaymentMethod('bank_transfer')}
                    className="mr-2"
                  />
                  <span>银行转账</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="alipay"
                    checked={paymentMethod === 'alipay'}
                    onChange={() => setPaymentMethod('alipay')}
                    className="mr-2"
                  />
                  <span>支付宝</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-4">
            <div className="border-b bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold">收货信息</h2>
            </div>
            
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">收货地址*</label>
                  <textarea
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    className="w-full p-2 border rounded"
                    rows={4}
                    required
                    placeholder="请输入详细的收货地址"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">联系电话*</label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="w-full p-2 border rounded"
                    required
                    placeholder="请输入联系电话"
                  />
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between mb-2">
                    <span>小计:</span>
                    <span>¥{calculateTotal()}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>配送费:</span>
                    <span>¥0.00</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mb-4">
                    <span>总计:</span>
                    <span>¥{calculateTotal()}</span>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-red-700 text-white py-3 rounded hover:bg-red-800"
                    disabled={cartItems.length === 0 || loading || loadingCreate}
                  >
                    {loadingCreate ? <Loader size="sm" /> : '提交订单'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;